import React from 'react';
import { Box, ButtonBase, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { getControlTokens } from './controlTokens';

/**
 * Route-backed page navigation for workspace top bars.
 * This intentionally has a stronger visual identity than action/filter controls.
 */
function WorkspacePageToggle({
  value,
  onChange,
  options = [],
  ariaLabel = 'Page view',
  compactBreakpoint,
  context = 'objectToolbar',
}) {
  const theme = useTheme();
  const tokens = getControlTokens(theme, context);
  const resolvedCompactBreakpoint = compactBreakpoint ?? (options.length > 3 ? 1180 : 760);
  const compact = useMediaQuery(`(max-width:${resolvedCompactBreakpoint}px)`);
  const railSurface = theme.workspace?.muted || tokens.surface;
  const railHeight = tokens.height + 2;

  return (
    <Box
      role="group"
      aria-label={ariaLabel}
      data-testid="workspace-page-toggle"
      data-compact={compact ? 'true' : 'false'}
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'stretch',
        gap: 0.25,
        height: railHeight,
        minHeight: railHeight,
        p: 0,
        border: `1px solid ${tokens.borderColor}`,
        borderRadius: `${tokens.radius + 4}px`,
        backgroundColor: railSurface,
        boxShadow: [
          `0 1px 3px ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.1)}`,
          `inset 0 1px 0 ${alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.07 : 0.65)}`,
        ].join(', '),
        flex: '0 0 auto',
        overflow: 'hidden',
      }}
    >
      {options.map((option) => {
        const selected = option.value === value;
        const label = compact ? option.shortLabel || option.label : option.label;
        const estimatedWidth = Math.max(58, Math.min(156, (label?.length || 0) * 6.4 + 45));
        const optionWidth = option.buttonWidth || estimatedWidth;

        return (
          <Tooltip key={option.value} title={option.tooltip || option.label || ''}>
            <ButtonBase
              type="button"
              disabled={option.disabled}
              aria-label={option.ariaLabel || option.tooltip || option.label}
              aria-pressed={selected}
              onClick={(event) => {
                if (selected) return;
                onChange?.(event, option.value);
              }}
              sx={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.65,
                width: optionWidth,
                minWidth: optionWidth,
                height: '100%',
                px: 0.8,
                borderRadius: `${tokens.radius + 3}px`,
                color: selected ? theme.palette.primary.main : theme.palette.text.secondary,
                backgroundColor: selected
                  ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.24 : 0.15)
                  : 'transparent',
                boxShadow: selected
                  ? `inset 0 0 0 0.5px ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.34 : 0.22)}`
                  : 'none',
                transition: 'color 140ms ease, background-color 140ms ease, box-shadow 140ms ease',
                overflow: 'hidden',
                '&::after': selected ? {
                  content: '""',
                  position: 'absolute',
                  left: 8,
                  right: 8,
                  bottom: 0,
                  height: 2,
                  borderRadius: '2px 2px 0 0',
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  pointerEvents: 'none',
                } : undefined,
                '&:hover': {
                  color: theme.palette.primary.main,
                  backgroundColor: selected
                    ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.24 : 0.14)
                    : alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.065),
                },
                '&:focus-visible': {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: 1,
                },
                '@media (prefers-reduced-motion: reduce)': {
                  transition: 'none',
                },
                ...option.sx,
              }}
            >
              <Box
                component="span"
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  width: 17,
                  height: 17,
                  borderRadius: '5px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: '0 0 auto',
                  color: theme.palette.primary.main,
                  backgroundColor: selected
                    ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.12)
                    : alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.1 : 0.065),
                  '& svg': {
                    width: 13,
                    height: 13,
                    fontSize: 13,
                  },
                }}
              >
                {option.icon}
              </Box>
              <Typography
                component="span"
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  minWidth: 0,
                  color: 'inherit',
                  fontSize: compact ? 10 : 10.5,
                  fontWeight: selected ? 800 : 700,
                  lineHeight: 1.05,
                  letterSpacing: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </Typography>
            </ButtonBase>
          </Tooltip>
        );
      })}
    </Box>
  );
}

export default WorkspacePageToggle;
